import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { colors } from '../../styles/theme';
import type { Role } from '../../lib/roleRouting';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { t } from '../../i18n/language';

interface NavItem {
  label: string;
  /** Which sense of the word, when the label means two things in English (see `t`). */
  meaning?: string;
  to: string;
  /** The heading this item sits under inside a dropdown (and the mobile sheet); consecutive items
   *  with the same group share one heading. */
  group?: string;
}

/** A section in the role's nav. Inline sections render as pills inline
 *  with the topbar; dropdown sections render as a button that opens a
 *  labelled panel of items. Splitting the nav into sections keeps the
 *  topbar scannable when a role (super_admin: 47 items, manager: 33)
 *  would otherwise overflow horizontally. */
type NavSection =
  | { kind: 'inline'; items: NavItem[] }
  | { kind: 'dropdown'; label: string; items: NavItem[] };

/** Client routes that only make sense after onboarding has been approved.
 *  Hidden from the nav until then; the backend rejects direct hits too. */
const CLIENT_POST_APPROVAL_PATHS = new Set<string>([
  '/client/portfolio',
  '/client/contracts',
  '/client/beneficiaries',
  '/client/fund',
  // Offered once there is something to withdraw: before approval the page can only say
  // that onboarding comes first.
  '/client/withdraw',
]);

/** Client routes that ONLY make sense BEFORE onboarding is approved.
 *  Once a client is approved the Onboarding wizard is a dead-end status
 *  page — clicking it costs a navigation and shows nothing actionable —
 *  so the nav entry is hidden. The route itself stays reachable in case
 *  staff deep-link a client back for a re-check. */
const CLIENT_PRE_APPROVAL_PATHS = new Set<string>([
  '/client/onboarding',
]);

/** The money staff work with, in the order it moves: in, out, then the controls that check and
 *  correct it (owner, 2026-11-01: the operational checks sit with the reconciliation they feed).
 *  One list for manager and super_admin. */
const MANAGER_INVESTMENTS: NavItem[] = [
  { label: 'Deposits', to: '/manager/deposits', group: 'Money in' },
  { label: 'Attribute deposit', to: '/manager/ops/attribute-deposit', group: 'Money in' },
  { label: 'Commitments', to: '/manager/commitments', group: 'Money in' },
  { label: 'Withdrawals', to: '/manager/withdrawals', group: 'Money out' },
  { label: 'Redemptions waiting', to: '/manager/ops/redemptions', group: 'Money out' },
  { label: 'Payouts', to: '/manager/ops/payouts', group: 'Money out' },
  { label: 'Reconciliation', to: '/manager/ops/reconciliation', group: 'Controls and corrections' },
  { label: 'Operational checks', to: '/manager/ops/checks', group: 'Controls and corrections' },
  { label: 'Reversals', to: '/manager/reversals', group: 'Controls and corrections' },
  { label: 'Deposit reversals', to: '/manager/deposit-reversals', group: 'Controls and corrections' },
];

/** Monitoring first (what can stop a client today), then the bank accounts money comes from, then
 *  the regulator's reports and the reference values they use, then the offering documents a
 *  purchaser is given. One list for manager and super_admin. */
const MANAGER_COMPLIANCE: NavItem[] = [
  { label: 'AML monitoring', to: '/manager/compliance/alerts', group: 'Monitoring' },
  { label: 'Sanctions', to: '/manager/compliance/sanctions', group: 'Monitoring' },
  { label: 'Blocked property', to: '/manager/compliance/blocked-property', group: 'Monitoring' },
  { label: 'SARs', to: '/manager/compliance/sars', group: 'Monitoring' },
  { label: 'Suspicion reports', to: '/manager/compliance/suspicion-reports', group: 'Monitoring' },
  { label: 'Client risk actions', to: '/manager/ops/client-risk', group: 'Monitoring' },
  { label: 'Account checks', to: '/manager/ops/bank-account-checks', group: 'Bank accounts' },
  { label: 'Source accounts', to: '/manager/ops/source-accounts', group: 'Bank accounts' },
  {
    label: 'Transfers from other accounts',
    to: '/manager/compliance/transfer-cases',
    group: 'Bank accounts',
  },
  { label: 'Regulatory reports', to: '/manager/compliance/regulatory-reports', group: 'Regulatory' },
  {
    label: 'Foreign investment registration',
    to: '/manager/compliance/foreign-investment',
    group: 'Regulatory',
  },
  { label: 'Data requests', to: '/manager/compliance/data-requests', group: 'Regulatory' },
  { label: 'Blocked countries', to: '/manager/compliance/blocked-countries', group: 'Regulatory' },
  { label: 'UMA value', to: '/manager/compliance/uma-value', group: 'Regulatory' },
  { label: 'Offering documents', to: '/manager/offering-documents', group: 'Offering' },
  { label: 'Rule 502(b) package', to: '/manager/offering-package', group: 'Offering' },
];

/**
 * IA, by how often a person needs a screen (owner, 2026-10-30: an order that makes sense and
 * helps navigation): the day's work stays inline as pills (the dashboard, the queue, the
 * clients or calls of the day), then the dropdowns in the order work flows: the money
 * (Investments), the controls on it (Compliance), the people, and the system configuration last.
 * Inside a dropdown, headings group the items (owner, 2026-11-01): by step in Investments (money
 * in, money out, controls and corrections), by subject in Compliance (monitoring, bank accounts,
 * regulatory, offering), clients and prospects before staff in People, and in System the products
 * and markets, then the documents and messages, then the platform.
 * Every registered screen a role can open is reachable from here, except
 * per-record pages (case, client 360, contract, product) which are reached
 * from their lists, and the profile / password / availability items that
 * live in the avatar menu (UserMenu).
 * Clients stay flat, in the order of what a partner does: the summary, then (before approval)
 * the onboarding, then what they hold, putting money in, taking it out, the agreements, the
 * trusted contacts, and the simulator last.
 */
const NAV_BY_ROLE: Record<Role, NavSection[]> = {
  super_admin: [
    {
      kind: 'inline',
      items: [
        { label: 'Admin', to: '/admin/dashboard' },
        { label: 'Operations', to: '/manager/dashboard' },
        { label: 'Review queue', to: '/manager/review-queue' },
        { label: 'Appointments', to: '/manager/appointments' },
      ],
    },
    { kind: 'dropdown', label: 'Investments', items: MANAGER_INVESTMENTS },
    { kind: 'dropdown', label: 'Compliance', items: MANAGER_COMPLIANCE },
    {
      kind: 'dropdown',
      label: 'People',
      items: [
        { label: 'Clients', to: '/admin/clients', group: 'Clients and prospects' },
        { label: 'Leads', to: '/manager/leads', group: 'Clients and prospects' },
        { label: 'Invitations', to: '/manager/invitations', group: 'Clients and prospects' },
        { label: 'Proposal campaigns', to: '/manager/proposal-campaigns', group: 'Clients and prospects' },
        { label: 'Team', to: '/manager/team', group: 'Staff' },
        { label: 'Staff', to: '/admin/staff', group: 'Staff' },
      ],
    },
    {
      kind: 'dropdown',
      label: 'System',
      items: [
        { label: 'Products', to: '/admin/products', group: 'Products and markets' },
        { label: 'New clients by product', to: '/admin/product-access', group: 'Products and markets' },
        { label: 'Countries', to: '/admin/jurisdictions', group: 'Products and markets' },
        {
          label: 'Concentration accounts',
          to: '/admin/concentration-accounts',
          group: 'Products and markets',
        },
        {
          label: 'Product commitment configs',
          to: '/admin/commitment-configs',
          group: 'Products and markets',
        },
        { label: 'Contract templates', to: '/admin/contract-templates', group: 'Documents and messages' },
        { label: 'Email templates', to: '/admin/email-templates', group: 'Documents and messages' },
        { label: 'Document wording', to: '/admin/document-wording', group: 'Documents and messages' },
        { label: 'Config health', to: '/admin/config-health', group: 'Platform' },
        { label: 'Maintenance', to: '/admin/maintenance', group: 'Platform' },
        { label: 'Audit log export', to: '/admin/audit-log-export', group: 'Platform' },
      ],
    },
  ],
  manager: [
    {
      kind: 'inline',
      items: [
        { label: 'Dashboard', to: '/manager/dashboard' },
        { label: 'Review queue', to: '/manager/review-queue' },
        { label: 'Appointments', to: '/manager/appointments' },
      ],
    },
    { kind: 'dropdown', label: 'Investments', items: MANAGER_INVESTMENTS },
    { kind: 'dropdown', label: 'Compliance', items: MANAGER_COMPLIANCE },
    {
      kind: 'dropdown',
      label: 'People',
      items: [
        { label: 'Leads', to: '/manager/leads', group: 'Clients and prospects' },
        { label: 'Invitations', to: '/manager/invitations', group: 'Clients and prospects' },
        { label: 'Proposal campaigns', to: '/manager/proposal-campaigns', group: 'Clients and prospects' },
        { label: 'Team', to: '/manager/team', group: 'Staff' },
      ],
    },
  ],
  agent: [
    {
      kind: 'inline',
      items: [
        { label: 'Dashboard', to: '/agent/dashboard' },
        { label: 'Review queue', to: '/agent/review-queue' },
        { label: 'Clients', to: '/agent/clients' },
        { label: 'Appointments', to: '/agent/appointments' },
      ],
    },
    {
      kind: 'dropdown',
      label: 'Investments',
      items: [
        { label: 'Deposits', to: '/agent/deposits', group: 'Money in' },
        { label: 'Attribute deposit', to: '/agent/ops/attribute-deposit', group: 'Money in' },
        { label: 'Commitments', to: '/agent/commitments', group: 'Money in' },
        { label: 'Withdrawals', to: '/agent/withdrawals', group: 'Money out' },
        { label: 'Payouts', to: '/agent/ops/payouts', group: 'Money out' },
        { label: 'Reconciliation', to: '/agent/ops/reconciliation', group: 'Reconciliation and corrections' },
        { label: 'Reversals', to: '/agent/reversals', group: 'Reconciliation and corrections' },
        { label: 'Deposit reversals', to: '/agent/deposit-reversals', group: 'Reconciliation and corrections' },
        { label: 'Offering questions', to: '/agent/offering-questions', group: 'Offering' },
      ],
    },
    {
      kind: 'dropdown',
      label: 'Compliance',
      items: [
        { label: 'Operational checks', to: '/agent/ops/checks' },
        { label: 'Account checks', to: '/agent/ops/bank-account-checks' },
      ],
    },
  ],
  client: [
    {
      kind: 'inline',
      items: [
        { label: 'Summary', to: '/client/dashboard' },
        { label: 'Onboarding', to: '/client/onboarding' },
        { label: 'Holdings', to: '/client/portfolio' },
        { label: 'Contribute', to: '/client/fund' },
        { label: 'Withdraw', to: '/client/withdraw' },
        { label: 'Contracts', to: '/client/contracts' },
        { label: 'Trusted contacts', to: '/client/beneficiaries' },
        { label: 'Simulator', to: '/simulator' },
      ],
    },
  ],
};

/** The role's menu, in order (read by the tests). */
export function navSectionsFor(role: Role): readonly NavSection[] {
  return NAV_BY_ROLE[role];
}

/** Items with the heading to print before each one (null when it continues the group above). */
function withHeadings(items: NavItem[]): { item: NavItem; heading: string | null }[] {
  return items.map((item, i) => ({
    item,
    heading: item.group && item.group !== items[i - 1]?.group ? item.group : null,
  }));
}

/** Whether the inline bar fits in the room left beside the brand, the search pill and the user
 *  menu. Measured on a hidden copy of the bar, so it holds in either language and at any width:
 *  a bar that does not fit collapses into the menu behind the hamburger instead of running under
 *  its neighbours, with no breakpoint guess that a longer Spanish label breaks. */
function useBarFits(signature: string): {
  roomRef: React.RefObject<HTMLDivElement>;
  measureRef: React.RefObject<HTMLDivElement>;
  fits: boolean;
} {
  const roomRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(false);
  useLayoutEffect(() => {
    const room = roomRef.current;
    const measure = measureRef.current;
    if (!room || !measure) return undefined;
    const check = (): void => setFits(measure.scrollWidth <= room.clientWidth);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(room);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [signature]);
  return { roomRef, measureRef, fits };
}

/** A dropdown panel that would run past the right edge of the window opens to the left instead. */
function keepInsideWindow(panel: HTMLDivElement | null): void {
  if (!panel) return;
  const right = panel.getBoundingClientRect().right;
  if (right > window.innerWidth - 8) {
    panel.style.left = 'auto';
    panel.style.right = '0';
  }
}

const PILL = 'px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap';

const Chevron = ({ open }: { open: boolean }): JSX.Element => (
  <svg
    aria-hidden="true"
    className="w-3 h-3 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface TopNavProps {
  role: string | null | undefined;
}

/**
 * Topbar navigation. Items are role-bound + filtered by the client's
 * onboarding state. When the whole bar fits beside its neighbours it renders inline (sections as
 * pills, dropdown sections as click-to-expand buttons); when it does not, it collapses into a
 * sheet anchored below the topbar with section and group headings.
 */
const TopNav = ({ role }: TopNavProps): JSX.Element | null => {
  const rawSections = (role && NAV_BY_ROLE[role as Role]) || [];
  const profile = useCurrentUserProfile();

  // Apply the client pre/post-approval filter to every section's items.
  // The filter runs per-section so dropdown contents stay coherent.
  const sections: NavSection[] = rawSections
    .map<NavSection>((s) => {
      if (role !== 'client') return s;
      const approved = profile?.client_onboarding_state === 'approved';
      const items = s.items.filter((i) =>
        approved
          ? !CLIENT_PRE_APPROVAL_PATHS.has(i.to)
          : !CLIENT_POST_APPROVAL_PATHS.has(i.to),
      );
      return s.kind === 'inline'
        ? { kind: 'inline', items }
        : { kind: 'dropdown', label: s.label, items };
    })
    .filter((s) => s.items.length > 0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownsRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const signature = sections
    .map((s) => (s.kind === 'inline' ? s.items.map((i) => i.to).join(',') : s.label))
    .join('|');
  const { roomRef, measureRef, fits } = useBarFits(signature);

  // Route change closes everything — mobile sheet AND any open dropdown.
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // The bar coming back inline closes the sheet it replaced.
  useEffect(() => {
    if (fits) setMobileOpen(false);
  }, [fits]);

  // Click-outside + Escape close any open dropdown.
  useEffect(() => {
    if (openDropdown === null) return;
    const onClick = (e: MouseEvent): void => {
      if (!dropdownsRef.current?.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown]);

  if (sections.length === 0) return null;

  const linkStyle = (isActive: boolean): React.CSSProperties => ({
    color: isActive ? colors.gold : 'rgba(255,255,255,0.65)',
    background: isActive ? 'rgba(212,168,75,0.10)' : 'transparent',
  });

  return (
    <>
      {/* The room the bar may use: everything between the brand and the search pill. The hidden
          copy inside it is what is measured; the real bar shows only when that copy fits. */}
      <div ref={roomRef} className="relative flex-1 min-w-0 flex items-center">
        {/* The trigger of the sheet, whenever the bar does not fit. Inside the room, so the room
            measures the same width with or without it. */}
        {!fits && (
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            data-menu="toggle"
            className="inline-flex shrink-0 items-center justify-center w-9 h-9 rounded-md transition-colors"
            style={{ background: mobileOpen ? 'rgba(255,255,255,0.08)' : 'transparent' }}
          >
            <svg aria-hidden="true" className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        )}
        {/* Clipped to nothing, so the copy never widens the page while it is measured. */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-0 overflow-hidden invisible pointer-events-none">
          <div ref={measureRef} className="absolute left-0 top-0 flex w-max items-center gap-1">
          {sections.flatMap((section) =>
            section.kind === 'inline'
              ? section.items.map((item) => (
                  <span key={item.to} className={PILL}>
                    {t(item.label, item.meaning)}
                  </span>
                ))
              : [
                  <span key={section.label} className={`${PILL} inline-flex items-center gap-1`}>
                    {section.label}
                    <Chevron open={false} />
                  </span>,
                ],
          )}
          </div>
        </div>
        {fits && (
          <nav className="flex items-center gap-1" ref={dropdownsRef} aria-label="Main menu" data-menu="bar">
            {sections.map((section) => {
              if (section.kind === 'inline') {
                return section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={PILL}
                    style={({ isActive }) => linkStyle(isActive)}
                  >
                    {t(item.label, item.meaning)}
                  </NavLink>
                ));
              }
              // Dropdown section — label is stable per-role so it makes a
              // safe React key + a safe id for the openDropdown state.
              const key = `dropdown-${section.label}`;
              const isOpen = openDropdown === key;
              // Highlight the dropdown button when any of its children is
              // the active route — so the user can find where they are even
              // when the dropdown is closed.
              const hasActiveChild = section.items.some(
                (i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`),
              );
              return (
                <div key={key} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(isOpen ? null : key)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    className={`${PILL} inline-flex items-center gap-1`}
                    style={linkStyle(hasActiveChild)}
                  >
                    {section.label}
                    <Chevron open={isOpen} />
                  </button>
                  {isOpen && (
                    <div
                      role="menu"
                      ref={keepInsideWindow}
                      className="absolute left-0 mt-1 py-1 min-w-[14rem] w-max max-w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-lg"
                      style={{
                        background: colors.navyLight,
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 12px 32px rgba(6,9,26,0.45)',
                        maxHeight: 'calc(100vh - 72px)',
                        zIndex: 50,
                      }}
                    >
                      {withHeadings(section.items).map(({ item, heading }) => (
                        <div key={item.to}>
                          {heading && (
                            <p
                              className="px-4 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider"
                              style={{ color: 'rgba(255,255,255,0.4)' }}
                            >
                              {heading}
                            </p>
                          )}
                          <NavLink
                            to={item.to}
                            role="menuitem"
                            onClick={() => setOpenDropdown(null)}
                            className="block px-4 py-2 text-sm font-medium transition-colors"
                            style={({ isActive }) => ({
                              color: isActive ? colors.gold : 'rgba(255,255,255,0.85)',
                              background: isActive ? 'rgba(212,168,75,0.10)' : 'transparent',
                            })}
                          >
                            {t(item.label, item.meaning)}
                          </NavLink>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </div>

      {/* The sheet — section and group headings replace the desktop dropdowns so the entire IA
          is reachable without a second tap, but the grouping stays visible. */}
      {!fits && mobileOpen && (
        <div
          className="absolute left-0 right-0 top-full"
          style={{
            background: colors.navy,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            maxHeight: 'calc(100vh - 56px)',
            overflowY: 'auto',
          }}
        >
          <nav className="px-4 py-3 flex flex-col gap-1" aria-label="Main menu" data-menu="sheet">
            {sections.map((section) => {
              // Stable key: dropdown sections have a label; inline
              // sections use their first item's path (a section's
              // items are stable per-role, so the first `to` is a
              // safe identifier).
              const key = section.kind === 'dropdown'
                ? `mobile-d-${section.label}`
                : `mobile-i-${section.items[0]?.to ?? 'empty'}`;
              return (
                <div key={key} className="mt-2 first:mt-0">
                  {section.kind === 'dropdown' && (
                    <p
                      className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: 'rgba(255,255,255,0.55)' }}
                    >
                      {section.label}
                    </p>
                  )}
                  {withHeadings(section.items).map(({ item, heading }) => (
                    <div key={item.to}>
                      {heading && (
                        <p
                          className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider"
                          style={{ color: 'rgba(255,255,255,0.35)' }}
                        >
                          {heading}
                        </p>
                      )}
                      <NavLink
                        to={item.to}
                        className="px-3 py-2.5 rounded-md text-sm font-medium transition-colors block"
                        style={({ isActive }) => ({
                          color: isActive ? colors.gold : 'rgba(255,255,255,0.75)',
                          background: isActive ? 'rgba(212,168,75,0.10)' : 'transparent',
                        })}
                      >
                        {t(item.label, item.meaning)}
                      </NavLink>
                    </div>
                  ))}
                </div>
              );
            })}
          </nav>
        </div>
      )}
    </>
  );
};

export default TopNav;
